import { useRef, useEffect, useCallback, useState } from "react";

const COLORS = ["#FF4D4D", "#7C5CFF", "#35C759", "#F5A623"];

export const DrawingCanvas = ({ active, strokes, onStrokesChange, color, className = "" }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(null);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { width: w, height: h } = canvas;
    ctx.clearRect(0, 0, w, h);
    for (const s of strokes || []) {
      if (!s.points?.length) continue;
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(s.points[0][0] * w, s.points[0][1] * h);
      for (const [x, y] of s.points.slice(1)) ctx.lineTo(x * w, y * h);
      ctx.stroke();
    }
  }, [strokes]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      redraw();
    };
    resize();
    const obs = new ResizeObserver(resize);
    obs.observe(canvas.parentElement);
    return () => obs.disconnect();
  }, [redraw]);

  useEffect(() => { redraw(); }, [redraw]);

  const norm = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return [(e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height];
  };

  const onDown = (e) => {
    if (!active) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = { color, points: [norm(e)] };
  };
  const onMove = (e) => {
    if (!drawing.current) return;
    drawing.current.points.push(norm(e));
    onStrokesChange([...(strokes || []), drawing.current]);
  };
  const onUp = () => {
    if (!drawing.current) return;
    onStrokesChange([...(strokes || []).filter((s) => s !== drawing.current), drawing.current]);
    drawing.current = null;
  };

  return (
    <canvas
      ref={canvasRef}
      data-testid="drawing-canvas"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      className={`absolute inset-0 w-full h-full ${active ? "cursor-crosshair" : "pointer-events-none"} ${className}`}
      style={{ touchAction: "none" }}
    />
  );
};

export const ColorPicker = ({ color, onChange }) => (
  <div className="flex gap-1.5">
    {COLORS.map((c) => (
      <button
        key={c}
        data-testid={`color-${c.slice(1)}`}
        onClick={() => onChange(c)}
        className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c ? "scale-110 border-white" : "border-transparent"}`}
        style={{ background: c }}
      />
    ))}
  </div>
);
